/* eslint-disable prettier/prettier */
import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { isEmpty } from 'lodash';
import {
  Card,
  CardContent,
  FormControl,
  MenuItem,
  RadioGroup,
  Select,
} from '@material-ui/core';
import Numeral from 'numeral';

import { Content, BeneficiosList } from '../../styledComponents';

export const TIPO_CAMBIO = {
  CELULAR: 'celular',
  TARJETA: 'tarjeta',
  SEGURO: 'seguro',
};

const onlyDigitsRegex = /^\d*$/;

const isValidLuhn = num => {
  const digits = String(num).replace(/\D/g, '');
  if (!digits) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = parseInt(digits[i], 10);
    if (shouldDouble) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

export const validateNumero = (tipoCambio, value) => {
  const trimmed = (value || '').trim();

  if (!trimmed) {
    return 'Este campo es obligatorio.';
  }

  if (!onlyDigitsRegex.test(trimmed)) {
    return 'Sólo se permiten números.';
  }

  if (tipoCambio === TIPO_CAMBIO.CELULAR) {
    if (trimmed.length !== 10) {
      return 'El número celular debe tener 10 dígitos.';
    }
    return '';
  }

  if (tipoCambio === TIPO_CAMBIO.TARJETA) {
    if (trimmed.length !== 16) {
      return 'El número de tarjeta debe tener 16 dígitos.';
    }
    if (!isValidLuhn(trimmed)) {
      return 'El número de tarjeta no es válido.';
    }
    return '';
  }

  return 'Tipo de cambio no soportado.';
};

const NumberInputStep = ({
  tipoCambio,
  numero,
  onNumeroChange,
  touched,
  onTouchedChange,
  bancos,
  bank,
  setBank,
  segurosDistribuidora,
  seguro,
  setSeguro,
}) => {
  const isCelular = tipoCambio === TIPO_CAMBIO.CELULAR;
  const maxLength = isCelular ? 10 : 16;
  let title = '';
  const label = isCelular
    ? 'Número celular (10 dígitos)'
    : 'Número de tarjeta (16 dígitos)';

  if (tipoCambio === TIPO_CAMBIO.CELULAR) {
    title = 'Ingresa tu número celular';
  } else if (tipoCambio === TIPO_CAMBIO.TARJETA) {
    title = 'Ingresa tu número de tarjeta';
  } else if (tipoCambio === TIPO_CAMBIO.SEGURO) {
    title = 'Selecciona tu protección Conmigo';
  }

  const placeholder = isCelular ? '5512345678' : '4111111111111111';

  const errorMessage = validateNumero(tipoCambio, numero);
  const showError = touched && Boolean(errorMessage);

  const handleChange = e => {
    const { value } = e.target;
    if (value === '' || onlyDigitsRegex.test(value)) {
      if (value.length <= maxLength) {
        onNumeroChange(value);
      }
    }
  };

  const handleBlur = () => {
    onTouchedChange(true);
  };

  return (
    <>
      <h1>{title}</h1>
      <Content style={{ padding: 0, marginTop: 12 }}>
        {(tipoCambio === 'tarjeta' || tipoCambio === 'celular') && (
          <TextField
            id="numero-input"
            label={label}
            value={numero}
            variant="outlined"
            fullWidth
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showError}
            helperText={showError ? errorMessage : ''}
            inputProps={{
              maxLength,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
        )}
        {!isEmpty(bancos) && tipoCambio === 'tarjeta' && (
          <FormControl variant="outlined" fullWidth>
            <Select
              labelId="bank-label"
              id="bank"
              value={bank}
              onChange={event => {
                setBank(event.target.value);
              }}
              displayEmpty
              inputProps={{ 'aria-label': 'Banco' }}
              style={{ color: bank ? '#212121' : '#a2a2a2' }}
              // disabled={inputsDisabled}
            >
              <MenuItem value="" disabled>
                Selecciona una opción
              </MenuItem>
              {bancos.map(c => (
                <MenuItem value={c.id} key={c.id}>
                  {c.nombre}
                </MenuItem>
              ))}
              {/* <FormHelperText>Banco</FormHelperText> */}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth></FormControl>
        {!isEmpty(segurosDistribuidora) && tipoCambio === 'seguro' && (
          <FormControl variant="outlined" fullWidth>
            <RadioGroup aria-label="options" name="options" value={seguro}>
              {segurosDistribuidora.map(option => (
                <Card
                  key={option.id}
                  variant="outlined"
                  style={{
                    marginBottom: '8px',
                    border: `3px solid ${
                      seguro === option.id ? '#994878' : '#00000042'
                    }`,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSeguro(option.id)}
                >
                  <CardContent>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ fontWeight: 'bold' }}>{option.nombre}</div>
                      <div style={{ color: '#994878' }}>
                        $ {option.cuota.toFixed(2)} quincenal
                      </div>
                    </div>
                    <div style={{ color: '#808080' }}>
                      ${Numeral(option.importeAsegurado).format('0,0')}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      {option.default && (
                        <div
                          style={{
                            marginLeft: 'auto',
                            backgroundColor: '#994878',
                            color: 'white',
                            fontWeight: 'bold',
                            borderRadius: '4px',
                            padding: '4px',
                            marginBottom: 'auto',
                          }}
                        >
                          ¡Recomendado!
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>{' '}
            {Boolean(seguro) && (
              <BeneficiosList>
                {segurosDistribuidora
                  .at(
                    segurosDistribuidora
                      .map(seguros => seguros.id)
                      .indexOf(seguro),
                  )
                  .beneficios.map(beneficio => (
                    <li key={beneficio.nombre}>
                      <strong>{beneficio.nombre}:</strong> {beneficio.beneficio}
                    </li>
                  ))}
              </BeneficiosList>
            )}
          </FormControl>
        )}
      </Content>
    </>
  );
};

NumberInputStep.propTypes = {
  tipoCambio: PropTypes.oneOf([
    TIPO_CAMBIO.CELULAR,
    TIPO_CAMBIO.TARJETA,
    TIPO_CAMBIO.SEGURO,
  ]),
  numero: PropTypes.string,
  onNumeroChange: PropTypes.func,
  touched: PropTypes.bool,
  onTouchedChange: PropTypes.func,
  bancos: PropTypes.array,
  bank: PropTypes.number,
  setBank: PropTypes.func,
  segurosDistribuidora: PropTypes.array,
  seguro: PropTypes.number,
  setSeguro: PropTypes.func,
};

NumberInputStep.defaultProps = {
  tipoCambio: TIPO_CAMBIO.CELULAR,
  numero: '',
  onNumeroChange: () => {},
  touched: false,
  onTouchedChange: () => {},
  bancos: [],
  bank: 0,
  setBank: () => {},
  segurosDistribuidora: [],
  seguro: '',
  setSeguro: () => {},
};

export default NumberInputStep;
